import { useState } from "react";
import { useForm } from "@inertiajs/react";
import {
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Divider,
  Tabs,
  Tab,
  Avatar,
  Chip,
} from "@heroui/react";
import Layout from "./Layouts/Layout";

export default function Profile({ user, error }) {
  const [activeTab, setActiveTab] = useState("email");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const emailForm = useForm({
    email: user.email,
    current_password: "",
  });

  const passwordForm = useForm({
    current_password: "",
    new_password: "",
    password_confirmation: "",
  });

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    emailForm.patch("/profile", {
      preserveScroll: true,
      onSuccess: () => {
        emailForm.reset("current_password");
      },
    });
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    passwordForm.patch("/profile", {
      preserveScroll: true,
      onSuccess: () => {
        passwordForm.reset();
      },
    });
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getPasswordStrength = (password) => {
    if (!password) return null;
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { label: "Weak", color: "danger" };
    if (strength <= 3) return { label: "Fair", color: "warning" };
    if (strength <= 4) return { label: "Good", color: "primary" };
    return { label: "Strong", color: "success" };
  };

  const passwordStrength = getPasswordStrength(passwordForm.data.new_password);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Profile settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your account security and preferences
          </p>
        </div>

        {/* User Info Card */}
        <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 mb-6">
          <CardBody className="p-6">
            <div className="flex items-center gap-6">
              <Avatar
                name={getInitials(user.name)}
                classNames={{
                  base: "w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 text-white text-2xl font-semibold",
                }}
              />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {user.name}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {user.email}
                </p>
                <div className="flex items-center gap-2 mt-3">
              <Chip
                size="sm"
                variant="flat"
                classNames={{
                  base: "bg-green-100 dark:bg-green-900/30",
                  content: "text-xs font-medium text-green-700 dark:text-green-400",
                }}
              >
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  Account active
                </span>
              </Chip>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200/50 dark:border-red-800/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <svg
                className="w-5 h-5 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Settings Card */}
        <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
          <CardHeader className="pb-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Security settings
            </h3>
          </CardHeader>
          <CardBody className="pt-4">
            <Tabs
              selectedKey={activeTab}
              onSelectionChange={setActiveTab}
              variant="underlined"
              classNames={{
                base: "w-full",
                tabList:
                  "gap-6 w-full relative rounded-none p-0 border-b border-gray-200 dark:border-gray-700",
                cursor: "w-full bg-amber-500",
                tab: "max-w-fit px-0 h-12",
                tabContent:
                  "text-gray-600 dark:text-gray-400 group-data-[selected=true]:text-amber-600 dark:group-data-[selected=true]:text-amber-400",
              }}
            >
              <Tab
                key="email"
                title={
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Change email
                  </div>
                }
              >
                <form
                  onSubmit={handleEmailSubmit}
                  className="flex flex-col gap-5 mt-6"
                >
                  <Input
                    type="email"
                    label="New email address"
                    placeholder="you@example.com"
                    value={emailForm.data.email}
                    onChange={(e) => emailForm.setData("email", e.target.value)}
                    isRequired
                    variant="bordered"
                    classNames={{
                      inputWrapper:
                        "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                    }}
                    description="Enter the new email address you want to use"
                    startContent={
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    }
                  />

                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    label="Current password"
                    placeholder="Enter your current password"
                    value={emailForm.data.current_password}
                    onChange={(e) =>
                      emailForm.setData("current_password", e.target.value)
                    }
                    isRequired
                    variant="bordered"
                    classNames={{
                      inputWrapper:
                        "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                    }}
                    description="Confirm your identity to change your email"
                    startContent={
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    }
                    endContent={
                      <button
                        type="button"
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showCurrentPassword ? (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        )}
                      </button>
                    }
                  />

                  <Button
                    type="submit"
                    size="lg"
                    isLoading={emailForm.processing}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40"
                    startContent={
                      !emailForm.processing && (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      )
                    }
                  >
                    {emailForm.processing ? "Updating..." : "Update email"}
                  </Button>
                </form>
              </Tab>

              <Tab
                key="password"
                title={
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                      />
                    </svg>
                    Change password
                  </div>
                }
              >
                <form
                  onSubmit={handlePasswordSubmit}
                  className="flex flex-col gap-5 mt-6"
                >
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    label="Current password"
                    placeholder="Enter your current password"
                    value={passwordForm.data.current_password}
                    onChange={(e) =>
                      passwordForm.setData("current_password", e.target.value)
                    }
                    isRequired
                    variant="bordered"
                    classNames={{
                      inputWrapper:
                        "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                    }}
                    startContent={
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    }
                    endContent={
                      <button
                        type="button"
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showCurrentPassword ? (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        )}
                      </button>
                    }
                  />

                  <div>
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      label="New password"
                      placeholder="Enter new password"
                      value={passwordForm.data.new_password}
                      onChange={(e) =>
                        passwordForm.setData("new_password", e.target.value)
                      }
                      isRequired
                      variant="bordered"
                      classNames={{
                        inputWrapper:
                          "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                      }}
                      description="Password must be at least 8 characters long"
                      startContent={
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                          />
                        </svg>
                      }
                      endContent={
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {showNewPassword ? (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          )}
                        </button>
                      }
                    />
                    {passwordStrength && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              passwordStrength.color === "danger"
                                ? "bg-red-500 w-1/4"
                                : passwordStrength.color === "warning"
                                  ? "bg-amber-500 w-2/4"
                                  : passwordStrength.color === "primary"
                                    ? "bg-blue-500 w-3/4"
                                    : "bg-green-500 w-full"
                            }`}
                          />
                        </div>
                        <Chip
                          size="sm"
                          variant="flat"
                          color={passwordStrength.color}
                        >
                          {passwordStrength.label}
                        </Chip>
                      </div>
                    )}
                  </div>

                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    label="Confirm new password"
                    placeholder="Confirm new password"
                    value={passwordForm.data.password_confirmation}
                    onChange={(e) =>
                      passwordForm.setData(
                        "password_confirmation",
                        e.target.value
                      )
                    }
                    isRequired
                    variant="bordered"
                    classNames={{
                      inputWrapper:
                        "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                    }}
                    isInvalid={
                      passwordForm.data.password_confirmation &&
                      passwordForm.data.new_password !==
                        passwordForm.data.password_confirmation
                    }
                    errorMessage={
                      passwordForm.data.password_confirmation &&
                      passwordForm.data.new_password !==
                        passwordForm.data.password_confirmation
                        ? "Passwords do not match"
                        : ""
                    }
                    startContent={
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    }
                    endContent={
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showConfirmPassword ? (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        )}
                      </button>
                    }
                  />

                  <Button
                    type="submit"
                    size="lg"
                    isLoading={passwordForm.processing}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40"
                    isDisabled={
                      passwordForm.data.new_password !==
                      passwordForm.data.password_confirmation
                    }
                    startContent={
                      !passwordForm.processing && (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                      )
                    }
                  >
                    {passwordForm.processing
                      ? "Updating..."
                      : "Update password"}
                  </Button>
                </form>
              </Tab>
            </Tabs>
          </CardBody>
        </Card>
      </div>
    </Layout>
  );
}
